<template>
  <div class="login-page" :class="{ 'login-page--host-brand': !!hostBrand }" :style="hostBrandVars">
    <v-card class="login-card pa-8" elevation="3">
          <!-- Logo/Brand -->
          <div class="text-center mb-6">
            <template v-if="hostBrand">
              <div class="host-brand-mark mb-3">{{ hostBrand.mark }}</div>
              <h1 class="text-h4 font-weight-bold host-brand-name">
                {{ hostBrand.name }}
              </h1>
              <p class="text-body-2 host-brand-tagline mt-1">{{ hostBrand.tagline }}</p>
              <p class="text-body-2 host-brand-muted mt-2">
                {{ domainSelectionMode ? 'Select Your Domain' : hostBrand.welcome }}
              </p>
            </template>
            <template v-else>
              <img :src="logo5Light" alt="FiveStarJob" class="login-brand-logo mb-2" />
              <h1 class="text-h4 font-weight-bold" style="color: #1e3a5f;">
                FiveStarJob
              </h1>
              <p class="text-body-2 text-secondary mt-2">
                {{ domainSelectionMode ? 'Select Your Domain' : 'Welcome back' }}
              </p>
            </template>
            <v-chip
              v-if="showNativeDevApiBanner"
              class="mt-3"
              color="warning"
              size="small"
              variant="flat"
              prepend-icon="mdi-flask-outline"
            >
              {{ nativeDevApiLabel }}
            </v-chip>
          </div>
          
          <!-- Domain Selection (multi-company users only; login form hidden) -->
          <div v-if="domainSelectionMode">
            <v-alert type="info" variant="tonal" class="mb-4" color="accent">
              <div class="text-body-2">
                You have access to multiple domains. Choose one to continue:
              </div>
            </v-alert>

            <v-alert v-if="error" type="error" variant="tonal" class="mb-4" rounded="lg">
              {{ error }}
            </v-alert>
            
            <v-list class="mb-4" :disabled="loading">
              <v-list-item
                v-for="domain in availableDomains"
                :key="domain.id"
                :disabled="loading"
                @click="selectDomain(domain.code)"
                class="mb-2 domain-item"
                rounded="lg"
                elevation="1"
              >
                <template v-slot:prepend>
                  <v-icon :color="domain.is_platform ? 'error' : 'primary'">
                    {{ domain.is_platform ? 'mdi-shield-crown' : 'mdi-domain' }}
                  </v-icon>
                </template>
                <v-list-item-title class="font-weight-medium">{{ domain.name }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ domain.code }}</v-list-item-subtitle>
                <template v-slot:append>
                  <v-chip v-if="domain.is_platform" color="error" size="small" variant="flat" class="mr-1">
                    HQ
                  </v-chip>
                  <v-chip v-if="domain.is_primary" color="primary" size="small" variant="flat">
                    Primary
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
            
            <v-btn block @click="cancelDomainSelection" variant="text" color="secondary" class="mt-4">
              Back to Login
            </v-btn>
          </div>
          
          <!-- Registration Form -->
          <v-form v-else-if="showRegister" ref="registerForm" v-model="registerValid" @submit.prevent="handleRegister">
            <PhoneNumberInput
              v-model="registerPhone"
              label="Phone Number"
              :use-company-default="false"
              default-country="US"
              required
              class="mb-4"
            />
            
            <v-text-field
              v-model="registerEmail"
              label="Email"
              placeholder="your@email.com"
              type="email"
              :rules="emailRules"
              prepend-inner-icon="mdi-email"
              required
              variant="outlined"
              class="mb-4"
              color="accent"
            ></v-text-field>
            
            <v-text-field
              v-model="registerFirstName"
              label="First Name"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              class="mb-4"
              color="accent"
            ></v-text-field>
            
            <v-text-field
              v-model="registerLastName"
              label="Last Name"
              prepend-inner-icon="mdi-account"
              variant="outlined"
              class="mb-4"
              color="accent"
            ></v-text-field>
            
            <v-text-field
              v-model="registerDomainCode"
              label="Domain Code"
              placeholder="techvill"
              :rules="domainCodeRules"
              prepend-inner-icon="mdi-domain"
              required
              variant="outlined"
              class="mb-4"
              color="accent"
              hint="Enter your company domain code"
              persistent-hint
            ></v-text-field>
            
            <v-alert v-if="error" type="error" variant="tonal" class="mb-6" rounded="lg">
              {{ error }}
            </v-alert>
            
            <v-alert v-if="registerSuccess" type="success" variant="tonal" class="mb-6" rounded="lg">
              {{ registerSuccess }}
            </v-alert>
            
            <v-btn
              type="submit"
              block
              color="accent"
              size="large"
              :loading="registerLoading"
              :disabled="!registerValid"
              class="mb-4"
              rounded="lg"
              elevation="2"
            >
              Submit Registration Request
            </v-btn>
            
            <v-btn
              block
              variant="text"
              color="secondary"
              @click="showRegister = false; error = ''; registerSuccess = ''"
              class="mb-4"
              rounded="lg"
            >
              Back to Login
            </v-btn>
          </v-form>
          
          <!-- Login Form -->
          <v-form v-else ref="form" v-model="valid" @submit.prevent="handleLogin">
            <v-text-field
              v-model="phone"
              label="Phone Number"
              placeholder="(415) 555-1234"
              :rules="phoneRules"
              prepend-inner-icon="mdi-phone"
              required
              variant="outlined"
              class="mb-4"
              color="accent"
              hint="Any format works — e.g. 415-555-1234, (415) 555-1234 or +14155551234"
              :persistent-hint="!phone"
              autocomplete="tel"
            ></v-text-field>
            
            <v-text-field
              v-if="!codeMode"
              v-model="password"
              label="Password"
              autocomplete="current-password"
              :type="showPassword ? 'text' : 'password'"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword"
              :rules="passwordRules"
              prepend-inner-icon="mdi-lock"
              required
              variant="outlined"
              class="mb-4"
              color="accent"
            ></v-text-field>

            <!-- Passwordless: SMS code field (shown after the code was sent) -->
            <v-text-field
              v-if="codeMode && codeSent"
              v-model="code"
              label="SMS code"
              placeholder="6-digit code"
              inputmode="numeric"
              autocomplete="one-time-code"
              prepend-inner-icon="mdi-message-text-lock"
              variant="outlined"
              class="mb-4"
              color="accent"
              hint="Enter the code we texted you"
              persistent-hint
            ></v-text-field>

            <!-- Technician Mode Toggle (hidden on native: the mobile app is
                 technician-only, so the office-mode choice is forced off) -->
            <v-card 
              v-if="!isNativeApp && !codeMode && showTechnicianToggle"
              variant="outlined" 
              class="mb-4 pa-3"
              :class="{ 'technician-mode-active': isTechnicianMode }"
            >
              <div class="d-flex align-center">
                <v-checkbox
                  v-model="isTechnicianMode"
                  hide-details
                  density="compact"
                  color="warning"
                  class="mr-2"
                >
                  <template v-slot:label>
                    <div class="d-flex align-center">
                      <v-icon 
                        size="20" 
                        :color="isTechnicianMode ? 'warning' : 'grey'" 
                        class="mr-2"
                      >
                        mdi-account-hard-hat
                      </v-icon>
                      <span :class="{ 'font-weight-medium': isTechnicianMode }">
                        I'm a Technician
                      </span>
                    </div>
                  </template>
                </v-checkbox>
              </div>
              <div v-if="isTechnicianMode" class="text-caption text-grey mt-2 ml-8">
                <v-icon size="12" class="mr-1">mdi-information</v-icon>
                You will be redirected to the mobile technician interface
              </div>
            </v-card>
            
            <v-alert v-if="error" type="error" variant="tonal" class="mb-6" rounded="lg">
              {{ error }}
            </v-alert>
            
            <v-btn
              v-if="!codeMode"
              type="submit"
              block
              color="accent"
              size="large"
              :loading="loading"
              :disabled="!valid"
              class="mb-4"
              rounded="lg"
              elevation="2"
            >
              Sign In
            </v-btn>

            <!-- Passwordless: request the SMS code -->
            <v-btn
              v-if="codeMode && !codeSent"
              block
              color="accent"
              size="large"
              :loading="codeLoading"
              :disabled="!phone"
              class="mb-4"
              rounded="lg"
              elevation="2"
              @click="handleRequestCode"
            >
              Send code by SMS
            </v-btn>

            <!-- Passwordless: verify the SMS code -->
            <template v-if="codeMode && codeSent">
              <v-btn
                block
                color="accent"
                size="large"
                :loading="codeLoading"
                :disabled="!code"
                class="mb-2"
                rounded="lg"
                elevation="2"
                @click="handleVerifyCode"
              >
                Verify &amp; sign in
              </v-btn>
              <v-btn
                block
                variant="text"
                color="secondary"
                size="small"
                :disabled="codeLoading"
                class="mb-4"
                @click="handleRequestCode"
              >
                Resend code
              </v-btn>
            </template>

            <!-- Toggle between password and SMS-code sign-in (office/web only) -->
            <div v-if="!isTechnicianMode" class="text-center mb-3">
              <a href="#" @click.prevent="toggleCodeMode" class="text-body-2 text-accent font-weight-medium">
                {{ codeMode ? 'Use password instead' : 'Sign in with a code instead' }}
              </a>
            </div>

            <!-- Biometric / passkey quick login (native only) -->
            <v-btn
              v-if="isNativeApp && quickLoginEnabled"
              block
              variant="outlined"
              color="primary"
              size="large"
              :loading="quickLoginLoading"
              class="mb-4"
              rounded="lg"
              :prepend-icon="biometricLoginIcon"
              @click="loginWithBiometrics"
            >
              Sign in with biometrics
            </v-btn>

            <div class="text-center mb-3">
              <router-link to="/forgot-password" class="text-body-2 text-secondary">
                Forgot password?
              </router-link>
            </div>

            <div v-if="isNativeApp && quickLoginEnabled" class="text-center mb-3">
              <a href="#" @click.prevent="disableQuickLogin" class="text-body-2 text-secondary">
                Disable biometric login
              </a>
            </div>
            
            <v-divider class="mb-4"></v-divider>
            
            <div v-if="showRegisterLink" class="text-center">
              <p class="text-body-2 text-secondary">
                Don't have an account?
                <a href="#" @click.prevent="openRegister" class="text-accent font-weight-medium">
                  Register
                </a>
              </p>
            </div>

            <div v-else-if="hostBrand" class="text-center">
              <a :href="hostBrand.homeUrl" class="text-body-2 host-brand-muted">
                {{ hostBrand.homeLabel }}
              </a>
            </div>
          </v-form>
    </v-card>
    
    <!-- Password Change Dialog -->
    <v-dialog v-model="showPasswordChangeDialog" max-width="500" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-h6 pa-4">
          <v-icon class="mr-2" color="warning">mdi-lock-reset</v-icon>
          Change Password Required
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-alert type="warning" variant="tonal" class="mb-4" rounded="lg">
            You must change your temporary password before continuing.
          </v-alert>
          
          <v-text-field
            v-model="newPassword"
            label="New Password"
            :type="showNewPassword ? 'text' : 'password'"
            :append-inner-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showNewPassword = !showNewPassword"
            :rules="passwordRules"
            prepend-inner-icon="mdi-lock"
            required
            variant="outlined"
            class="mb-4"
            color="accent"
          ></v-text-field>
          
          <v-text-field
            v-model="confirmPassword"
            label="Confirm New Password"
            type="password"
            :rules="[v => !!v || 'Please confirm password', v => v === newPassword || 'Passwords do not match']"
            prepend-inner-icon="mdi-lock-check"
            required
            variant="outlined"
            class="mb-4"
            color="accent"
          ></v-text-field>
          
          <v-alert v-if="passwordChangeError" type="error" variant="tonal" class="mb-4" rounded="lg">
            {{ passwordChangeError }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="accent"
            variant="flat"
            @click="handlePasswordChange"
            :loading="changingPassword"
            :disabled="!newPassword || !confirmPassword || newPassword.length < 8"
            rounded="lg"
          >
            Change Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onActivated } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import axios, { LOGIN_NOTICE_KEY } from '@/api/client'
import { isIOS, isNative } from '@/native/platform'
import { openInAppBrowser } from '@/native/inAppBrowser'
import { getNonProductionApiLabel, isNonProductionApi } from '@/utils/apiOrigin'
import { clearAccessToken, loadStoredTokens } from '@/auth/tokenManager'
import {
  verifyIdentity,
  deleteCredentials,
  getTokenQuickLoginPhone,
} from '@/native/biometric'
import {
  clearPasskeyLocal,
  isPasskeyLoginAvailable,
} from '@/native/passkey'
import {
  getBiometricLoginStatus,
  isQuickLoginOffered,
  resolveQuickLoginPhone,
  restoreTokenBiometricIfConfigured,
  ensureBiometricConfiguredMigrated,
} from '@/native/biometricLoginStatus'
import { getBiometricConfiguredPhone, markBiometricLoginConfigured } from '@/native/biometricEnrollment'
import { enableTokenQuickLoginDetailed } from '@/native/biometric'
import { queueBiometricEnrollment, setBiometricDismissed } from '@/native/biometricEnrollment'
import { getLastLoginPhone, setLastLoginPhone } from '@/native/lastLoginPhone'
import { consumeAccountDeletedFlag } from '@/composables/useDeleteAccount'
import { useNotify } from '@/composables/useNotify'
import PhoneNumberInput from '@/components/base/PhoneNumberInput.vue'
import { resolveHostBrand, hostBrandStyle, ensureHostBrandFonts } from '@/config/hostBrands'
// Same 3D "5 + star" brand mark as the sidebar header (light variant — the
// login card is always on a white surface).
import logo5Light from '@/assets/brand/logo5-light.png'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { notify, confirm, alert } = useNotify()

// A tenant reaching us through its own domain (e.g. pageonelife.com) signs in
// on a screen styled after that domain, not after FiveStarJob. Null on every
// host that is not registered, which keeps the default look untouched.
const hostBrand = resolveHostBrand()
const hostBrandVars = hostBrandStyle(hostBrand)
const showTechnicianToggle = hostBrand ? hostBrand.showTechnicianToggle !== false : true
const showRegisterLink = hostBrand ? hostBrand.showRegister !== false : true

// Biometric / passkey quick-login (native only).
const quickLoginEnabled = ref(false)
const quickLoginLoading = ref(false)

// Native (Capacitor) build is the technician-only mobile app: force technician
// login + redirect to /technician, and hide the office-mode toggle. Web is
// unchanged (the toggle stays, defaulting to office mode).
const isNativeApp = isNative()
// AP-7-00437: iOS presents Face ID as the quick-login affordance. Android
// keeps the fingerprint glyph; this is presentation only and does not alter
// either platform's existing biometric/passkey authentication path.
const biometricLoginIcon = isIOS() ? 'mdi-face-recognition' : 'mdi-fingerprint'
const showNativeDevApiBanner = isNativeApp && isNonProductionApi()
const nativeDevApiLabel = getNonProductionApiLabel()

// After login, honor a ?redirect= deep link (e.g. a ticket link from
// Telegram), but only if it is a safe in-app path.
// Office login destination: honor an explicit ?redirect= deep link, otherwise
// send owners of a company in "Demo mode" to the celebratory Welcome page first
// (the same operator toggle that magic-link login reads). Best-effort — any
// failure falls back to the dashboard so login is never blocked.
async function resolvePostLoginTarget() {
  const explicit = route.query.redirect
  if (typeof explicit === 'string' && explicit.startsWith('/') &&
      !explicit.startsWith('//') && !explicit.startsWith('/login')) {
    return explicit
  }
  try {
    const { data } = await axios.get('/domains/quick-launch/')
    if (data?.demo_welcome) return '/welcome'
  } catch (e) {
    console.log('[Login] demo_welcome check skipped:', e?.response?.status)
  }
  return '/dashboard'
}

const form = ref(null)
const valid = ref(false)
const phone = ref('')
const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const error = ref('')
const showRegister = ref(false)
// Public site (fivestarjob.com); the app itself is served from fivestarjob.ca.
const MARKETING_SITE_URL = 'https://fivestarjob.com/'
const isTechnicianMode = ref(isNativeApp)

// Passwordless sign-in via SMS code (Twilio Verify)
const codeMode = ref(false)
const codeSent = ref(false)
const code = ref('')
const codeLoading = ref(false)

// Registration form
const registerForm = ref(null)
const registerValid = ref(false)
const registerPhone = ref('')
const registerEmail = ref('')
const registerFirstName = ref('')
const registerLastName = ref('')
const registerDomainCode = ref('')
const registerLoading = ref(false)
const registerSuccess = ref('')

// Password change dialog
const showPasswordChangeDialog = ref(false)
const newPassword = ref('')
const confirmPassword = ref('')
const showNewPassword = ref(false)
const changingPassword = ref(false)
const passwordChangeError = ref('')

// Domain selection
const domainSelectionMode = ref(false)
const availableDomains = ref([])
const tempCredentials = ref(null)

function normalizePhone(value) {
  if (!value) return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 10) return '+1' + digits
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits
  if (digits.length >= 11 && digits.length <= 15) return '+' + digits
  return value
}

const phoneRules = [
  v => !!v || 'Phone is required',
  v => {
    const digits = (v || '').replace(/\D/g, '')
    if (digits.length >= 10 && digits.length <= 15) return true
    return 'Enter at least 10 digits'
  }
]

const passwordRules = [
  v => !!v || 'Password is required'
]

const emailRules = [
  v => !!v || 'Email is required',
  v => /.+@.+\..+/.test(v) || 'Email must be valid'
]

const domainCodeRules = [
  v => !!v || 'Domain code is required'
]

// On native, show quick-login when passkey is registered or token unlock is active.
async function refreshQuickLoginState() {
  clearAccessToken()
  try {
    await loadStoredTokens()
    await ensureBiometricConfiguredMigrated()
    const loginPhone = await resolveQuickLoginPhone(normalizePhone(phone.value))
    if (loginPhone) phone.value = loginPhone

    quickLoginEnabled.value = await isQuickLoginOffered(loginPhone)
  } catch {
    quickLoginEnabled.value = false
  }
}

/** Re-bind refresh token to biometric unlock after password login. */
async function rebindBiometricAfterPasswordLogin(normalizedPhone) {
  if (!isNativeApp || !normalizedPhone) return
  await loadStoredTokens()
  const configured = getBiometricConfiguredPhone()
  const sameAccount = configured && (
    configured === normalizedPhone
    || configured.replace(/\D/g, '') === normalizedPhone.replace(/\D/g, '')
  )
  if (sameAccount) {
    const result = await enableTokenQuickLoginDetailed(normalizedPhone)
    if (result.ok) {
      markBiometricLoginConfigured(normalizedPhone)
      return
    }
  }
  await restoreTokenBiometricIfConfigured(normalizedPhone)
}

async function maybeShowAccountDeletedDialog() {
  if (!consumeAccountDeletedFlag()) return
  await alert({
    title: 'Your account was successfully deleted',
    message: '',
    confirmText: 'Close',
    type: 'success',
  })
}

onMounted(async () => {
  // The tab title is owned by the branding store (it repaints on every
  // loadPublic), so only the web fonts are pulled in here.
  if (hostBrand) ensureHostBrandFonts(hostBrand)
  try {
    const notice = sessionStorage.getItem(LOGIN_NOTICE_KEY)
    if (notice) {
      sessionStorage.removeItem(LOGIN_NOTICE_KEY)
      error.value = notice
      console.log('[Login] session ended with notice:', notice)
    }
  } catch (_) { /* ignore */ }
  await maybeShowAccountDeletedDialog()
  if (!isNativeApp) return
  await authStore.purgeZombieSessionIfNeeded()
  await refreshQuickLoginState()
})

onActivated(async () => {
  await maybeShowAccountDeletedDialog()
  if (!isNativeApp) return
  await refreshQuickLoginState()
})

async function loginWithBiometrics() {
  if (quickLoginLoading.value) return
  quickLoginLoading.value = true
  error.value = ''
  try {
    const loginPhone = await resolveQuickLoginPhone(normalizePhone(phone.value))
    if (!loginPhone) {
      error.value = 'No saved login found. Please sign in with your password.'
      quickLoginEnabled.value = false
      return
    }
    phone.value = loginPhone

    let status = await getBiometricLoginStatus(loginPhone)

    if (status.mode === 'needs_reauth') {
      await loadStoredTokens()
      status = await getBiometricLoginStatus(loginPhone)
      if (status.mode === 'needs_reauth') {
        notify.info(
          'Enter your password and tap Sign In once. Fingerprint login will work again on the next screen.',
        )
        return
      }
    }

    // Passkey path (includes platform biometric prompt) — works after Sign Out.
    if (status.mode === 'passkey') {
      const passkeyReady = await Promise.race([
        isPasskeyLoginAvailable(loginPhone),
        new Promise((resolve) => setTimeout(() => resolve(false), 6000)),
      ])
      if (passkeyReady) {
        const result = await authStore.technicianPasskeyLogin(loginPhone)
        if (result.needsDomainSelection) {
          domainSelectionMode.value = true
          availableDomains.value = result.domains
          tempCredentials.value = {
            phone: loginPhone,
            authMode: 'passkey',
            passkey_session_token: result.passkey_session_token,
          }
          return
        }
        if (!result.error) {
          setLastLoginPhone(loginPhone)
          await nextTick()
          await new Promise(resolve => setTimeout(resolve, 100))
          router.push('/technician')
          return
        }
        error.value = result.error
      }
    }

    // Token-gated biometric quick login (refresh token in secure storage).
    if (status.mode === 'token_unlock') {
      const ok = await verifyIdentity({ reason: 'Sign in to FiveStarJob' })
      if (!ok) {
        if (!error.value) error.value = 'Biometric verification was cancelled or failed.'
        return
      }
      const result = await authStore.technicianBiometricTokenLogin()
      if (result.needsDomainSelection) {
        domainSelectionMode.value = true
        availableDomains.value = result.domains
        tempCredentials.value = {
          phone: (await getTokenQuickLoginPhone()) || loginPhone || authStore.user?.phone_number,
          authMode: 'token',
        }
        return
      }
      if (!result.error) {
        const hint = (await getTokenQuickLoginPhone()) || authStore.user?.phone_number
        if (hint) setLastLoginPhone(hint)
        await nextTick()
        await new Promise(resolve => setTimeout(resolve, 100))
        router.push('/technician')
        return
      }
      error.value = result.error
      if (result.error?.includes('expired')) {
        await deleteCredentials()
        quickLoginEnabled.value = false
      }
      return
    }

    error.value = 'No saved login found. Please sign in with your password.'
    quickLoginEnabled.value = false
  } catch {
    error.value = 'Biometric login failed. Please use your password.'
  } finally {
    quickLoginLoading.value = false
  }
}

async function disableQuickLogin() {
  await deleteCredentials()
  await clearPasskeyLocal()
  quickLoginEnabled.value = false
  setBiometricDismissed()
  authStore.clearAuth()
  notify.info('Biometric login disabled')
}

function toggleCodeMode() {
  codeMode.value = !codeMode.value
  codeSent.value = false
  code.value = ''
  error.value = ''
}

async function handleRequestCode() {
  const normalizedPhone = normalizePhone(phone.value)
  if (!normalizedPhone) {
    error.value = 'Enter your phone number first'
    return
  }
  phone.value = normalizedPhone
  codeLoading.value = true
  error.value = ''
  try {
    await axios.post('/auth/login/request-code/', { phone_number: normalizedPhone })
    codeSent.value = true
    notify.success('If this number has an account, a login code was sent by SMS.')
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not send the code. Please try again.'
  } finally {
    codeLoading.value = false
  }
}

async function handleVerifyCode() {
  const normalizedPhone = normalizePhone(phone.value)
  const enteredCode = (code.value || '').trim()
  if (!normalizedPhone || !enteredCode) {
    error.value = 'Enter the code from the SMS'
    return
  }
  codeLoading.value = true
  error.value = ''
  try {
    const result = await authStore.loginWithCode(normalizedPhone, enteredCode)
    if (result.needsDomainSelection) {
      domainSelectionMode.value = true
      availableDomains.value = result.domains
      tempCredentials.value = {
        phone: normalizedPhone,
        authMode: 'code',
        login_ticket: result.login_ticket,
      }
    } else if (result.error) {
      error.value = result.error
    } else {
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 100))
      router.push(await resolvePostLoginTarget())
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Invalid or expired code.'
  } finally {
    codeLoading.value = false
  }
}

async function handleLogin() {
  // Code mode uses its own handlers; ignore the password-form submit.
  if (codeMode.value) return
  if (!valid.value) return

  loading.value = true
  error.value = ''

  const normalizedPhone = normalizePhone(phone.value)
  // reflect normalized form back to the field so the user sees the canonical phone
  if (normalizedPhone && normalizedPhone !== phone.value) {
    phone.value = normalizedPhone
  }

  try {
    // If technician mode, use technician login endpoint
    if (isTechnicianMode.value) {
      const result = await authStore.technicianLogin(normalizedPhone, password.value)

      if (result.error) {
        error.value = result.error
        return
      }

      if (result.needsDomainSelection) {
        // Multi-company technician: dedicated company picker; single-domain
        // users never hit this branch (backend returns tokens immediately).
        console.log('[Login] technician needs domain selection')
        domainSelectionMode.value = true
        availableDomains.value = result.domains
        tempCredentials.value = { phone: normalizedPhone, password: password.value }
        password.value = ''
        return
      }

      // Stash credentials so the technician shell can offer biometric
      // enrollment AFTER it mounts. Login navigation must not be blocked.
      await rebindBiometricAfterPasswordLogin(normalizedPhone)
      queueBiometricEnrollment(normalizedPhone)
      setLastLoginPhone(normalizedPhone)
      password.value = ''
      await nextTick()
      await router.push('/technician')
      return
    }

    // Standard login
    const result = await authStore.login(normalizedPhone, password.value)

    if (result.needsDomainSelection) {
      domainSelectionMode.value = true
      availableDomains.value = result.domains
      tempCredentials.value = { phone: normalizedPhone, password: password.value }
      password.value = ''
    } else {
      // Login successful - wait for Vue to update state, then redirect
      // This is critical for PWA where state changes may not propagate immediately
      await nextTick()
      // Small delay to ensure auth state is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100))
      router.push(await resolvePostLoginTarget())
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Login failed. Please check your credentials.'
  } finally {
    loading.value = false
  }
}

async function selectDomain(domainCode) {
  loading.value = true
  error.value = ''

  try {
    const mode = tempCredentials.value?.authMode

    // Passkey: user already verified — complete with company + session ticket.
    if (mode === 'passkey') {
      const result = await authStore.technicianPasskeySelectDomain(
        tempCredentials.value.phone,
        tempCredentials.value.passkey_session_token,
        domainCode,
      )
      if (result.error) {
        error.value = result.error
        return
      }
      domainSelectionMode.value = false
      availableDomains.value = []
      setLastLoginPhone(tempCredentials.value.phone)
      queueBiometricEnrollment(tempCredentials.value.phone)
      tempCredentials.value = null
      await nextTick()
      await router.push('/technician')
      return
    }

    // SMS-code login: complete with the chosen company using the short-lived
    // login ticket (the code itself was already consumed in step 1).
    if (mode === 'code') {
      const result = await authStore.loginWithCode(
        tempCredentials.value.phone,
        null,
        domainCode,
        tempCredentials.value.login_ticket,
      )
      if (result.error) {
        error.value = result.error
        return
      }
      if (result.needsDomainSelection) {
        error.value = 'Please choose a company to continue.'
        return
      }
      domainSelectionMode.value = false
      availableDomains.value = []
      tempCredentials.value = null
      code.value = ''
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 100))
      router.push(await resolvePostLoginTarget())
      return
    }

    // Token biometric unlock: switch/confirm company with existing session.
    if (mode === 'token') {
      const result = await authStore.technicianSelectDomain(domainCode)
      if (result.error) {
        error.value = result.error
        return
      }
      domainSelectionMode.value = false
      availableDomains.value = []
      if (tempCredentials.value?.phone) setLastLoginPhone(tempCredentials.value.phone)
      tempCredentials.value = null
      await nextTick()
      await router.push('/technician')
      return
    }

    if (isTechnicianMode.value) {
      // Technician flow: re-login with the chosen company, go to the tech app.
      const result = await authStore.technicianLogin(
        tempCredentials.value.phone,
        tempCredentials.value.password,
        domainCode
      )
      if (result.error) {
        error.value = result.error
        return
      }
      if (result.needsDomainSelection) {
        error.value = 'Please choose a company to continue.'
        return
      }
      domainSelectionMode.value = false
      availableDomains.value = []
      await rebindBiometricAfterPasswordLogin(tempCredentials.value.phone)
      queueBiometricEnrollment(tempCredentials.value.phone)
      setLastLoginPhone(tempCredentials.value.phone)
      tempCredentials.value = null
      password.value = ''
      await nextTick()
      await router.push('/technician')
      return
    }

    await authStore.login(
      tempCredentials.value.phone,
      tempCredentials.value.password,
      domainCode
    )

    // Wait for Vue to update state (critical for PWA)
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 100))
    router.push(await resolvePostLoginTarget())
  } catch (err) {
    error.value = err.response?.data?.error || 'Login failed.'
  } finally {
    loading.value = false
  }
}

function cancelDomainSelection() {
  const mode = tempCredentials.value?.authMode
  domainSelectionMode.value = false
  availableDomains.value = []
  tempCredentials.value = null
  password.value = ''
  code.value = ''
  codeSent.value = false
  error.value = ''
  // Token path may have left a half-open session — clear it on cancel.
  if (mode === 'token' || mode === 'passkey') {
    try { authStore.clearAuth() } catch { /* ignore */ }
  }
}

// The mobile app is technician-only and creates no accounts: on native the
// Register link opens the marketing site in SFSafariViewController (iOS) /
// Chrome Custom Tabs (Android). Web keeps the in-app sign-up form.
async function openRegister() {
  if (!isNativeApp) {
    showRegister.value = true
    return
  }
  await openInAppBrowser(MARKETING_SITE_URL)
}

async function handleRegister() {
  if (!registerValid.value) return
  
  registerLoading.value = true
  error.value = ''
  registerSuccess.value = ''
  
  const normalizedRegPhone = normalizePhone(registerPhone.value)
  if (normalizedRegPhone && normalizedRegPhone !== registerPhone.value) {
    registerPhone.value = normalizedRegPhone
  }

  try {
    const response = await axios.post('/auth/register/', {
      phone_number: normalizedRegPhone,
      email: registerEmail.value,
      first_name: registerFirstName.value || '',
      last_name: registerLastName.value || '',
      domain_code: registerDomainCode.value
      // Password not required - admin will set temporary password when approving
    })
    
    registerSuccess.value = response.data.message || 'Registration request submitted successfully. Please wait for administrator approval.'
    
    // Reset form
    registerPhone.value = ''
    registerEmail.value = ''
    registerFirstName.value = ''
    registerLastName.value = ''
    registerDomainCode.value = ''
    
    // Hide registration form after 3 seconds
    setTimeout(() => {
      showRegister.value = false
      registerSuccess.value = ''
    }, 5000)
  } catch (err) {
    console.error('Registration error:', err)
    error.value = err.response?.data?.error || 'Registration failed. Please try again.'
  } finally {
    registerLoading.value = false
  }
}

async function handlePasswordChange() {
  if (!newPassword.value || newPassword.value.length < 8) {
    passwordChangeError.value = 'Password must be at least 8 characters'
    return
  }
  
  if (newPassword.value !== confirmPassword.value) {
    passwordChangeError.value = 'Passwords do not match'
    return
  }
  
  changingPassword.value = true
  passwordChangeError.value = ''
  
  try {
    // TODO: Create API endpoint for password change
    // For now, we'll use the user profile update endpoint
    await axios.patch('/auth/me/', {
      password: newPassword.value
    })
    
    // Clear password change requirement
    authStore.user.requires_password_change = false
    authStore.updateUser({ requires_password_change: false })
    
    showPasswordChangeDialog.value = false
    newPassword.value = ''
    confirmPassword.value = ''
    
    // Wait for Vue to update state (critical for PWA)
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 100))
    router.push('/dashboard')
  } catch (err) {
    console.error('Password change error:', err)
    passwordChangeError.value = err.response?.data?.error || 'Failed to change password. Please try again.'
  } finally {
    changingPassword.value = false
  }
}
</script>

<style scoped>
.login-page {
  background: #f5f7fa;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 480px;
}

.login-brand-logo {
  height: 64px;
  width: auto;
  object-fit: contain;
}

/* Sign-in screen for a tenant's own domain: the tokens come from that domain's
   marketing site (see config/hostBrands.js) and stay inside this page. */
.login-page--host-brand {
  background: var(--brand-host-bg);
  font-family: var(--brand-host-font);
}

/* Vuetify re-declares the whole --v-theme-* set on every element carrying a
   theme class (the card does), which would shadow the brand tokens set inline
   on this page. Re-inherit the three we override so the card and its buttons
   keep the brand's palette. */
.login-page--host-brand :deep(.v-theme--light),
.login-page--host-brand :deep(.v-theme--dark) {
  --v-theme-primary: inherit;
  --v-theme-accent: inherit;
  --v-theme-on-accent: inherit;
}

.login-page--host-brand .login-card {
  max-width: 460px;
  border: 1px solid var(--brand-host-line);
  border-radius: 20px;
  background: var(--brand-host-surface);
}

.login-page--host-brand :deep(.v-btn) {
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
}

.host-brand-mark {
  width: 56px;
  height: 56px;
  margin: 0 auto;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgb(var(--v-theme-primary)), #274872);
  color: rgb(var(--v-theme-accent));
  font-family: var(--brand-host-display);
  font-size: 26px;
  font-weight: 700;
}

.host-brand-name {
  color: var(--brand-host-ink);
  font-family: var(--brand-host-display);
  letter-spacing: -0.02em;
}

.host-brand-tagline {
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.host-brand-muted {
  color: var(--brand-host-muted);
}

.domain-item {
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.domain-item:hover {
  background: #f9fafb;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.technician-mode-active {
  border-color: #ff9800 !important;
  background: rgba(255, 152, 0, 0.05);
}

/* Chrome autofill fills the input without firing an input event, so Vuetify
   keeps the inline label overlapping the autofilled value. Hide the inline
   label while the field is autofilled but not yet marked active. */
:deep(.v-field:not(.v-field--active):has(input:-webkit-autofill) .v-label.v-field-label) {
  visibility: hidden;
}

</style>
